import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Save, Plus, Trash2, Edit2, Globe, Type, FileText, Link, Image,
  RefreshCw, CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContentEditor = ({ token }) => {
  const [content, setContent] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [editingItem, setEditingItem] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/content`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContent(response.data.content || {});
    } catch (error) {
      console.error('Error loading content:', error);
      if (error.response?.status === 404 || Object.keys(content).length === 0) {
        // Try to initialize
        await initializeContent();
      } else {
        toast.error('Fehler beim Laden des Contents');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const initializeContent = async () => {
    try {
      await axios.post(`${API}/admin/content/init`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Content-Einstellungen initialisiert');
      await loadContent();
    } catch (error) {
      console.error('Error initializing content:', error);
      toast.error('Fehler beim Initialisieren');
    }
  };

  const handleSave = async (item) => {
    try {
      await axios.put(
        `${API}/admin/content/${item.id}`,
        {
          de: item.de,
          en: item.en,
          es: item.es,
          order: item.order
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Content gespeichert');
      setEditingItem(null);
      await loadContent();
    } catch (error) {
      console.error('Error saving content:', error);
      toast.error('Fehler beim Speichern');
    }
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Wirklich löschen?')) return;
    
    try {
      await axios.delete(`${API}/admin/content/${itemId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Content gelöscht');
      await loadContent();
    } catch (error) {
      console.error('Error deleting content:', error);
      toast.error('Fehler beim Löschen');
    }
  };

  const handleCreate = async (newItem) => {
    try {
      await axios.post(
        `${API}/admin/content`,
        newItem,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Content erstellt');
      setShowAddModal(false);
      await loadContent();
    } catch (error) {
      console.error('Error creating content:', error);
      toast.error('Fehler beim Erstellen');
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case 'text': return <Type size={16} />;
      case 'textarea': return <FileText size={16} />;
      case 'url': return <Link size={16} />;
      case 'image': return <Image size={16} />;
      default: return <Type size={16} />;
    }
  };

  const getSectionTitle = (section) => {
    const titles = {
      'hero': '🎯 Hero Section',
      'steps': '📋 How It Works',
      'cta': '🚀 Call-to-Action',
      'footer': '📄 Footer'
    };
    return titles[section] || section;
  };

  if (isLoading) {
    return (
      <div className="content-editor-loading">
        <RefreshCw className="spin" size={48} />
        <p>Lade Content-Einstellungen...</p>
      </div>
    );
  }

  return (
    <div className="content-editor">
      <div className="content-editor-header">
        <div>
          <h2>Content Management</h2>
          <p>Verwalte Landing Page Texte, Überschriften und mehr</p>
        </div>
        <button className="btn-primary" onClick={() => setShowAddModal(true)}>
          <Plus size={20} />
          Neuen Content hinzufügen
        </button>
      </div>

      {Object.keys(content).length === 0 ? (
        <div className="content-empty">
          <Globe size={64} color="#9CA3AF" />
          <h3>Keine Content-Einstellungen gefunden</h3>
          <p>Initialisiere die Standard-Einstellungen</p>
          <button className="btn-primary" onClick={initializeContent}>
            <Plus size={20} />
            Content initialisieren
          </button>
        </div>
      ) : (
        <div className="content-sections">
          {Object.entries(content).map(([section, items]) => (
            <div key={section} className="content-section">
              <h3 className="section-title">{getSectionTitle(section)}</h3>
              
              <div className="content-items">
                {items.map((item) => (
                  <div key={item.id} className="content-item">
                    <div className="content-item-header">
                      <div className="content-item-meta">
                        {getIcon(item.type)}
                        <span className="content-key">{item.key}</span>
                        <span className="content-type-badge">{item.type}</span>
                      </div>
                      <div className="content-item-actions">
                        <button
                          className="btn-icon"
                          onClick={() => setEditingItem(item)}
                          title="Bearbeiten"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          className="btn-icon btn-danger"
                          onClick={() => handleDelete(item.id)}
                          title="Löschen"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>

                    {editingItem?.id === item.id ? (
                      <div className="content-item-edit">
                        <div className="content-language-tabs">
                          <span className="lang-label">🇩🇪 Deutsch</span>
                        </div>
                        {item.type === 'textarea' ? (
                          <textarea
                            value={editingItem.de}
                            onChange={(e) =>
                              setEditingItem({ ...editingItem, de: e.target.value })
                            }
                            rows={3}
                          />
                        ) : (
                          <input
                            type="text"
                            value={editingItem.de}
                            onChange={(e) =>
                              setEditingItem({ ...editingItem, de: e.target.value })
                            }
                          />
                        )}

                        <div className="content-language-tabs">
                          <span className="lang-label">🇬🇧 English</span>
                        </div>
                        {item.type === 'textarea' ? (
                          <textarea
                            value={editingItem.en}
                            onChange={(e) =>
                              setEditingItem({ ...editingItem, en: e.target.value })
                            }
                            rows={3}
                          />
                        ) : (
                          <input
                            type="text"
                            value={editingItem.en}
                            onChange={(e) =>
                              setEditingItem({ ...editingItem, en: e.target.value })
                            }
                          />
                        )}

                        <div className="content-language-tabs">
                          <span className="lang-label">🇪🇸 Español</span>
                        </div>
                        {item.type === 'textarea' ? (
                          <textarea
                            value={editingItem.es}
                            onChange={(e) =>
                              setEditingItem({ ...editingItem, es: e.target.value })
                            }
                            rows={3}
                          />
                        ) : (
                          <input
                            type="text"
                            value={editingItem.es}
                            onChange={(e) =>
                              setEditingItem({ ...editingItem, es: e.target.value })
                            }
                          />
                        )}

                        <div className="content-edit-actions">
                          <button
                            className="btn-secondary"
                            onClick={() => setEditingItem(null)}
                          >
                            Abbrechen
                          </button>
                          <button
                            className="btn-primary"
                            onClick={() => handleSave(editingItem)}
                          >
                            <Save size={16} />
                            Speichern
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="content-item-preview">
                        <div className="content-preview-lang">
                          <strong>🇩🇪</strong> {item.de}
                        </div>
                        <div className="content-preview-lang">
                          <strong>🇬🇧</strong> {item.en}
                        </div>
                        <div className="content-preview-lang">
                          <strong>🇪🇸</strong> {item.es}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Modal */}
      {showAddModal && (
        <AddContentModal
          onClose={() => setShowAddModal(false)}
          onCreate={handleCreate}
        />
      )}
    </div>
  );
};

// Add Content Modal Component
const AddContentModal = ({ onClose, onCreate }) => {
  const [newItem, setNewItem] = useState({
    section: 'hero',
    key: '',
    de: '',
    en: '',
    es: '',
    type: 'text',
    order: 0
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onCreate(newItem);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h3>Neuen Content hinzufügen</h3>
        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label>Section</label>
              <select
                value={newItem.section}
                onChange={(e) => setNewItem({ ...newItem, section: e.target.value })}
                required
              >
                <option value="hero">Hero</option>
                <option value="steps">Steps</option>
                <option value="cta">CTA</option>
                <option value="footer">Footer</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div className="form-group">
              <label>Key</label>
              <input
                type="text"
                value={newItem.key}
                onChange={(e) => setNewItem({ ...newItem, key: e.target.value })}
                placeholder="z.B. title, subtitle"
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Type</label>
              <select
                value={newItem.type}
                onChange={(e) => setNewItem({ ...newItem, type: e.target.value })}
              >
                <option value="text">Text</option>
                <option value="textarea">Textarea</option>
                <option value="url">URL</option>
                <option value="image">Image</option>
              </select>
            </div>

            <div className="form-group">
              <label>Reihenfolge</label>
              <input
                type="number"
                value={newItem.order}
                onChange={(e) => setNewItem({ ...newItem, order: parseInt(e.target.value) })}
              />
            </div>
          </div>

          <div className="form-group">
            <label>🇩🇪 Deutsch</label>
            <input
              type="text"
              value={newItem.de}
              onChange={(e) => setNewItem({ ...newItem, de: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label>🇬🇧 English</label>
            <input
              type="text"
              value={newItem.en}
              onChange={(e) => setNewItem({ ...newItem, en: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label>🇪🇸 Español</label>
            <input
              type="text"
              value={newItem.es}
              onChange={(e) => setNewItem({ ...newItem, es: e.target.value })}
              required
            />
          </div>

          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Abbrechen
            </button>
            <button type="submit" className="btn-primary">
              <Plus size={16} />
              Erstellen
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContentEditor;
